import { useEffect, useReducer, useRef } from 'react';
import { Level } from '../../types/level';
import { Word } from '../../types/word';
import { validateAll } from '../../types/levelUtils';
import useAppContext from '../../hooks/useAppContext';

type Action =
  | { type: 'add'; value: string }
  | { type: 'remove'; value: string }
  | { type: 'toggleLock'; value: string }
  | { type: 'flip'; value: string }
  | { type: 'move'; value: string; ax: number; ay: number }
  | { type: 'validate'; invalid: string[] }
  | { type: 'apply'; level: Level };

const reducer = (state: Level, action: Action): Level => {
  switch (action.type) {
    case 'add':
      const words = state.words.concat(new Word(action.value));
      return { ...state, words: words };
    case 'remove':
      return {
        ...state,
        words: state.words.filter((w) => w.word !== action.value),
      };
    case 'toggleLock':
      return {
        ...state,
        words: state.words.map((w) => {
          const isLocked = w.word === action.value ? !w.metadata.isLocked : w.metadata.isLocked;
          return { ...w, metadata: { ...w.metadata, isLocked: isLocked } };
        }),
      };
    case 'flip':
      return {
        ...state,
        words: state.words.map((w) => {
          const isVertical =
            w.word === action.value && !w.metadata.isLocked
              ? !w.metadata.isVertical
              : w.metadata.isVertical;
          return { ...w, metadata: { ...w.metadata, isVertical: isVertical } };
        }),
      };
    case 'move':
      return {
        ...state,
        words: state.words.map((w) => {
          if (w.word !== action.value || w.metadata.isLocked) {
            return { ...w };
          }

          return {
            ...w,
            metadata: { ...w.metadata, x: action.ax, y: action.ay },
          };
        }),
      };
    case 'validate':
      return {
        ...state,
        words: state.words.map((w) => {
          return { ...w, isValid: action.invalid.indexOf(w.word) === -1 };
        }),
      };
    case 'apply':
      return { ...action.level };
    default:
      return state;
  }
};

export const useLevelReducer = () => {
  const { Lang } = useAppContext();
  const [level, dispatch] = useReducer(reducer, new Level(Lang));
  const validated = useRef<boolean>(false);

  const onSelect = (value: string, selected: boolean): void => {
    dispatchChanges({ type: selected ? 'add' : 'remove', value: value });
  };

  const onToggleLock = (value: string): void => {
    dispatchChanges({ type: 'toggleLock', value: value });
  };

  const onFlip = (value: string): void => {
    dispatchChanges({ type: 'flip', value: value });
  };

  const onMove = (value: string, ax: number, ay: number): void => {
    dispatchChanges({ type: 'move', value: value, ax: ax, ay: ay });
  };

  const onApply = (lvl: Level): void => {
    dispatchChanges({ type: 'apply', level: lvl });
  };

  const dispatchChanges = (value: Action) => {
    dispatch(value);
    validated.current = false;
  };

  useEffect(() => {
    if (!validated.current) {
      validated.current = true;
      const result = validateAll(level);
      dispatch({ type: 'validate', invalid: result });
    }
  }, [level]);

  return {
    level,
    onSelect,
    onToggleLock,
    onFlip,
    onMove,
    onApply,
  };
};
